import { Box, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  root: {
    minHeight: "100vh",
    background: "var(--background-color)",
    color: "var(--text-color)",
    padding: "40px 24px",
    maxWidth: 860,
    margin: "0 auto",
  },
  section: {
    marginTop: 28,
  },
  subsection: {
    marginTop: 16,
  },
  highlight: {
    background: "var(--list-item-sel-color, #f0f4ff)",
    borderLeft: "4px solid #3f51b5",
    padding: "10px 16px",
    borderRadius: 4,
    margin: "12px 0",
  },
  contact: {
    marginTop: 8,
    lineHeight: 1.8,
  },
}));

export default function PrivacyPolicy() {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" className="subtitleStyle" style={{ color: "#808080" }}>
        Effective Date: April 19, 2026 &nbsp;|&nbsp; Last Updated: April 19, 2026
      </Typography>
      <Divider style={{ margin: "16px 0" }} />

      {/* 1 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>1. Introduction</Typography>
        <Typography variant="body2">
          Medlearnity ("we," "us," or "our") operates the QLearn platform accessible at
          https://www.medlearnity.com and through our mobile applications (collectively, the "Service").
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information
          when you use our Service.
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          By using the Service, you agree to the collection and use of information in accordance
          with this Privacy Policy.
        </Typography>
      </Box>

      {/* 2 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>2. Information We Collect</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>2.1 Personal Information</Typography>
          <Typography variant="body2">We may collect the following personal information when you register or use our Service:</Typography>
          <ul>
            <li><Typography variant="body2">Full name</Typography></li>
            <li><Typography variant="body2">Email address</Typography></li>
            <li><Typography variant="body2">Phone number (when provided for SMS verification)</Typography></li>
            <li><Typography variant="body2">Account credentials (username, password hash)</Typography></li>
            <li><Typography variant="body2">Profile information (specialty, exam type, study preferences)</Typography></li>
          </ul>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>2.2 Usage Data</Typography>
          <Typography variant="body2">We automatically collect information about how you interact with the Service, including:</Typography>
          <ul>
            <li><Typography variant="body2">Pages visited and features used</Typography></li>
            <li><Typography variant="body2">Study session duration and quiz performance</Typography></li>
            <li><Typography variant="body2">Device type, operating system, and browser</Typography></li>
            <li><Typography variant="body2">IP address and approximate location (country/state level)</Typography></li>
          </ul>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>2.3 Payment Information</Typography>
          <Typography variant="body2">
            Payment transactions are processed by Stripe. We do not store full credit card numbers.
            We retain only the last four digits of your card and transaction identifiers.
          </Typography>
        </Box>
      </Box>

      {/* 3 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>3. How We Use Your Information</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>3.1 Phone Numbers and SMS</Typography>
          <Typography variant="body2">When you provide your phone number, we use it <strong>solely</strong> for the following purposes:</Typography>
          <ul>
            <li><Typography variant="body2">Sending one-time passcodes (OTP) for account verification</Typography></li>
            <li><Typography variant="body2">Sending one-time passcodes for login authentication</Typography></li>
            <li><Typography variant="body2">Account security alerts at your request</Typography></li>
          </ul>
          <Typography variant="body2"><strong>We do not use your phone number for marketing, promotional, or advertising messages.</strong></Typography>
          <Box className={classes.highlight}>
            <Typography variant="body2">
              Your mobile opt-in data and phone number will not be shared with or sold to
              third parties or affiliates for marketing or promotional purposes.
            </Typography>
          </Box>
          <Typography variant="body2">
            SMS messages are sent only to users who have explicitly opted in via their account
            settings. You may opt out at any time by replying <strong>STOP</strong> to any SMS message from us,
            or by turning off SMS notifications in your account preferences.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>3.2 General Use of Personal Information</Typography>
          <Typography variant="body2">We use the information we collect to:</Typography>
          <ul>
            <li><Typography variant="body2">Create and manage your account</Typography></li>
            <li><Typography variant="body2">Provide, operate, and improve the Service</Typography></li>
            <li><Typography variant="body2">Authenticate your identity and secure your account</Typography></li>
            <li><Typography variant="body2">Respond to customer support requests</Typography></li>
            <li><Typography variant="body2">Send transactional emails (account confirmations, password resets)</Typography></li>
            <li><Typography variant="body2">Comply with legal obligations</Typography></li>
          </ul>
        </Box>
      </Box>

      {/* 4 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>4. How We Share Your Information</Typography>
        <Typography variant="body2">We do not sell, trade, or rent your personal information to third parties.</Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>We may share your information only in the following limited circumstances:</Typography>
        <ul>
          <li><Typography variant="body2"><strong>Service providers:</strong> Trusted vendors who assist in operating our Service (e.g., cloud hosting, payment processing, email delivery), bound by confidentiality agreements</Typography></li>
          <li><Typography variant="body2"><strong>Legal compliance:</strong> When required by law, court order, or governmental authority</Typography></li>
          <li><Typography variant="body2"><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, with prior notice to users</Typography></li>
          <li><Typography variant="body2"><strong>With your consent:</strong> For any other purpose with your explicit consent</Typography></li>
        </ul>
        <Box className={classes.highlight}>
          <Typography variant="body2">
            Mobile opt-in data (phone numbers and SMS consent) will never be shared with
            third parties for marketing or promotional purposes under any circumstances.
          </Typography>
        </Box>
      </Box>

      {/* 5 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>5. SMS Communications</Typography>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.1 Opt-In</Typography>
          <Typography variant="body2">
            You opt in to SMS communications by entering your phone number and enabling SMS
            verification in your QLearn account settings.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.2 Message Frequency</Typography>
          <Typography variant="body2">
            Message frequency depends on your account activity. OTP messages are sent only when
            you initiate a verification or login action. You will not receive unsolicited messages.
          </Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.3 Message and Data Rates</Typography>
          <Typography variant="body2">Standard message and data rates may apply depending on your mobile carrier plan.</Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.4 Opt-Out</Typography>
          <Typography variant="body2">To stop receiving SMS messages from QLearn:</Typography>
          <ul>
            <li><Typography variant="body2">Reply <strong>STOP</strong> to any SMS message from us</Typography></li>
            <li><Typography variant="body2">Or toggle off SMS in your account Preferences page</Typography></li>
          </ul>
          <Typography variant="body2">You will receive a final confirmation message upon opt-out.</Typography>
        </Box>

        <Box className={classes.subsection}>
          <Typography variant="subtitle2" gutterBottom>5.5 Help</Typography>
          <Typography variant="body2">For assistance, reply <strong>HELP</strong> to any SMS message or contact us at:</Typography>
          <ul>
            <li><Typography variant="body2">Email: support@medlearnity.com</Typography></li>
            <li><Typography variant="body2">Phone: +1 (631) 540-2499</Typography></li>
          </ul>
        </Box>
      </Box>

      {/* 6 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>6. Data Retention</Typography>
        <Typography variant="body2">
          We retain your personal information for as long as your account is active or as needed
          to provide the Service. Phone numbers associated with opted-out accounts are removed
          from our active SMS list within 24 hours of opt-out. You may request deletion of your
          account and associated data by contacting support@medlearnity.com.
        </Typography>
      </Box>

      {/* 7 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>7. Data Security</Typography>
        <Typography variant="body2">We implement industry-standard security measures to protect your information, including:</Typography>
        <ul>
          <li><Typography variant="body2">Encryption in transit (TLS/HTTPS)</Typography></li>
          <li><Typography variant="body2">Encrypted storage for sensitive fields</Typography></li>
          <li><Typography variant="body2">OTP codes are hashed and never stored in plain text</Typography></li>
          <li><Typography variant="body2">Access controls and audit logging</Typography></li>
        </ul>
        <Typography variant="body2">
          No method of transmission over the internet is 100% secure. We cannot guarantee
          absolute security but are committed to protecting your data.
        </Typography>
      </Box>

      {/* 8 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>8. Children's Privacy</Typography>
        <Typography variant="body2">
          Our Service is not directed to individuals under the age of 18. We do not knowingly
          collect personal information from children under 18. If we become aware that a child
          has provided us with personal information, we will delete it promptly.
        </Typography>
      </Box>

      {/* 9 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>9. Your Rights</Typography>
        <Typography variant="body2">Depending on your jurisdiction, you may have the right to:</Typography>
        <ul>
          <li><Typography variant="body2">Access the personal information we hold about you</Typography></li>
          <li><Typography variant="body2">Request correction of inaccurate data</Typography></li>
          <li><Typography variant="body2">Request deletion of your data</Typography></li>
          <li><Typography variant="body2">Withdraw consent at any time (including SMS opt-out)</Typography></li>
          <li><Typography variant="body2">Lodge a complaint with a data protection authority</Typography></li>
        </ul>
        <Typography variant="body2">To exercise these rights, contact us at support@medlearnity.com.</Typography>
      </Box>

      {/* 10 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>10. Changes to This Privacy Policy</Typography>
        <Typography variant="body2">
          We may update this Privacy Policy from time to time. We will notify you of material
          changes by posting the new policy on this page and updating the "Last Updated" date.
          Continued use of the Service after changes constitutes acceptance of the updated policy.
        </Typography>
      </Box>

      {/* 11 */}
      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>11. Contact Us</Typography>
        <Typography variant="body2" className={classes.contact}>
          <strong>Medlearnity</strong><br />
          15608 Riverside Dr W, Apt 5F<br />
          New York, NY 10032<br />
          United States<br />
          Email: support@medlearnity.com<br />
          Phone: +1 (631) 540-2499<br />
          Website: https://www.medlearnity.com
        </Typography>
      </Box>

      <Box style={{ marginTop: 40, marginBottom: 16 }}>
        <Divider />
      </Box>
    </Box>
  );
}
